import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./floating-panel.scss";

// Native top layer avoids ancestor overflow/transform/z-index constraints.
// Dialogs keep focus in the editor; menus remain non-modal.
export default function FloatingPanel({
  anchorRef, point, modal = false, width = 360, minWidth = 0, maxHeight = 560,
  gap = 6, pad = 8, align = "start", placement = "below", dismissOnOutside = false, label, children, onClose,
  className = "", onKeyDown, initialFocus = "input, button:not(:disabled)",
}) {
  const panelRef = useRef(null);
  const backdropPress = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const previous = document.activeElement;
    if (modal) panel.showModal();
    else if (panel.showPopover) panel.showPopover();

    const update = () => {
      const viewport = window.visualViewport;
      const x = viewport?.offsetLeft || 0;
      const y = viewport?.offsetTop || 0;
      const w = viewport?.width || document.documentElement.clientWidth;
      const h = viewport?.height || document.documentElement.clientHeight;
      panel.style.maxWidth = `${Math.max(0, w - pad * 2)}px`;
      panel.style.maxHeight = `${Math.max(0, Math.min(maxHeight, h - pad * 2))}px`;
      const rect = anchorRef?.current?.getBoundingClientRect();
      if (!point && !rect) return;
      const box = panel.getBoundingClientRect();
      const left = point ? point.x : placement === "cover" ? rect.left - gap : align === "end" ? rect.right - box.width : rect.left;
      const below = point ? point.y : rect.bottom + gap;
      const above = (point ? point.y : rect.top - gap) - box.height;
      const top = placement === "cover" && rect ? rect.top - gap : below + box.height <= y + h - pad ? below : above;
      setPosition({
        left: Math.max(x + pad, Math.min(left, x + w - box.width - pad)),
        top: Math.max(y + pad, Math.min(top, y + h - box.height - pad)),
      });
    };
    update();
    panel.style.visibility = "visible";
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    if (anchorRef?.current) observer.observe(anchorRef.current);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    const focus = panel.querySelector(initialFocus);
    (focus || panel).focus({ preventScroll: true });
    if (focus?.tagName === "INPUT" && focus.type === "text") focus.select();

    const outside = (event) => {
      if (!modal && !event.composedPath().includes(panel) &&
          !anchorRef?.current?.contains(event.target)) closeRef.current?.();
    };
    const escape = (event) => {
      if (!modal && event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current?.();
      }
    };
    document.addEventListener("pointerdown", outside, true);
    panel.addEventListener("keydown", escape);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      document.removeEventListener("pointerdown", outside, true);
      panel.removeEventListener("keydown", escape);
      if (modal && panel.open) panel.close();
      else if (panel.hidePopover && panel.matches(":popover-open")) panel.hidePopover();
      // Do not steal focus from another control clicked outside a menu.
      if (modal || panel.contains(document.activeElement) || document.activeElement === document.body) {
        if (previous?.isConnected) previous.focus({ preventScroll: true });
      }
    };
  }, [anchorRef, point, modal, width, maxHeight, gap, pad, align, placement, initialFocus]);

  const isBackdrop = (event) => {
    if (event.target !== event.currentTarget) return false;
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom;
  };
  if (typeof document === "undefined") return null;
  const Tag = modal ? "dialog" : "div";
  return createPortal(
    <Tag ref={panelRef} popover={modal ? undefined : "manual"}
      className={`ant-floating-panel ${className}`}
      role={modal ? undefined : "menu"} tabIndex={-1} aria-label={label}
      onCancel={(event) => { event.preventDefault(); closeRef.current?.("escape"); }}
      onPointerDown={(event) => {
        backdropPress.current = modal && dismissOnOutside && event.button === 0 && isBackdrop(event);
      }}
      onPointerUp={(event) => {
        if (backdropPress.current && isBackdrop(event)) closeRef.current?.("outside");
        backdropPress.current = false;
      }}
      onPointerCancel={() => { backdropPress.current = false; }}
      onKeyDown={onKeyDown}
      style={{ width, minWidth: Math.min(minWidth, width), ...position,
        visibility: position ? "visible" : "hidden" }}>
      {children}
    </Tag>, document.body,
  );
}
